import { Editor, Extension } from '@tiptap/core'
import type { Range } from '@tiptap/core'
import { PluginKey } from '@tiptap/pm/state'
import Suggestion, { type SuggestionProps, type SuggestionKeyDownProps } from '@tiptap/suggestion'
import { VueRenderer } from '@tiptap/vue-3'
import tippy, { Instance, Props } from 'tippy.js'

import type { Group } from '@/types'

import MenuList from './CommandsList.vue'
import { renderGroups } from './groups'

interface SlashCommandOptions {
  getCommandGroups?: (options: { editor: Editor; presetGroups: Group[] }) => Group[]
}

const extensionName = 'slashCommand'
let popup: Instance<Props>[] | null = null

export const SlashCommand = Extension.create<SlashCommandOptions>({
  name: extensionName,
  priority: 200,
  onCreate() {
    popup = tippy('body', {
      interactive: true,
      trigger: 'manual',
      placement: 'bottom-start',
      theme: 'slash-command',
      maxWidth: '16rem',
      offset: [16, 8],
      zIndex: 9999,
      popperOptions: {
        strategy: 'fixed',
        modifiers: [
          {
            name: 'flip',
            enabled: false,
          },
        ],
      },
      onCreate(instance) {
        instance.popper.classList.add('ProseMirror')
      },
    })
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        char: '/',
        allowSpaces: true,
        startOfLine: true,
        pluginKey: new PluginKey(extensionName),
        allow: ({ state, range }) => {
          const allowedActives = ['backgroundBlock', 'column']
          const $from = state.doc.resolve(range.from)
          const isRootDepth = $from.depth === 1
          const isStartOfNode = $from.parent.textContent?.charAt(0) === '/'
          const isInActiveAllowed = allowedActives.some(active => this.editor.isActive(active))
          const afterContent = $from.parent.textContent?.substring($from.parent.textContent?.indexOf('/'))
          const isValidAfterContent = !afterContent?.endsWith('  ')
          return ((isRootDepth && isStartOfNode) || (isInActiveAllowed && isStartOfNode)) && isValidAfterContent
        },
        command: ({ editor, range, props }: { editor: Editor; range: Range; props }) => {
          const { view } = editor
          props.action({ editor, range })
          view.focus()
        },
        items: ({ query, editor }: { query: string; editor: Editor }) => {
          const presetGroups = renderGroups()
          const groups = this.options.getCommandGroups?.({ editor, presetGroups }) || presetGroups
          const withFilteredCommands = groups.map(group => ({
            ...group,
            commands: group.commands
              .filter(item => {
                const labelNormalized = item.label.toLowerCase().trim()
                const queryNormalized = query.toLowerCase().trim()

                if (item.aliases) {
                  const aliases = item.aliases.map(alias => alias.toLowerCase().trim())
                  const labelMatch = labelNormalized.match(queryNormalized)
                  const aliasMatch = aliases.some(alias => alias.match(queryNormalized))

                  return labelMatch || aliasMatch
                }

                return labelNormalized.match(queryNormalized)
              })
              .filter(command => (command.shouldBeHidden ? !command.shouldBeHidden(this.editor) : true)),
          }))
          // Remove empty groups
          const withoutEmptyGroups = withFilteredCommands.filter(group => {
            return group.commands.length > 0
          })

          return withoutEmptyGroups.map(group => ({
            ...group,
            commands: group.commands.map(command => ({
              ...command,
              isEnabled: true,
            })),
          }))
        },
        render: () => {
          let component: VueRenderer | null = null
          let scrollHandler: (() => void) | null = null
          return {
            onStart: (props: SuggestionProps) => {
              component = new VueRenderer(MenuList, {
                props,
                editor: props.editor,
              })
              const { view } = props.editor
              const getReferenceClientRect = () => {
                if (!props.clientRect) {
                  return props.editor.storage[extensionName].rect
                }

                const rect = props.clientRect()

                if (!rect) {
                  return props.editor.storage[extensionName].rect
                }

                let yPos = rect.y
                const el = component?.element as HTMLElement

                if (rect.top + el.offsetHeight + 40 > window.innerHeight) {
                  const diff = rect.top + el.offsetHeight - window.innerHeight + 40
                  yPos = rect.y - diff
                }

                return new DOMRect(rect.x, yPos, rect.width, rect.height)
              }

              scrollHandler = () => {
                popup?.[0].setProps({
                  getReferenceClientRect,
                })
              }

              view.dom.parentElement?.addEventListener('scroll', scrollHandler)

              popup?.[0].setProps({
                getReferenceClientRect,
                appendTo: () => document.body,
                content: component.element as Element,
              })

              popup?.[0].show()
            },

            onUpdate(props: SuggestionProps) {
              component?.updateProps(props)

              const { view } = props.editor

              const getReferenceClientRect = () => {
                if (!props.clientRect) {
                  return props.editor.storage[extensionName].rect
                }

                const rect = props.clientRect()

                if (!rect) {
                  return props.editor.storage[extensionName].rect
                }

                // Account for when the editor is bound inside a container that doesn't go all the way to the edge of the screen
                return new DOMRect(rect.x, rect.y, rect.width, rect.height)
              }

              let scrollHandler = () => {
                popup?.[0].setProps({
                  getReferenceClientRect,
                })
              }

              view.dom.parentElement?.addEventListener('scroll', scrollHandler)

              props.editor.storage[extensionName].rect = props.clientRect
                ? getReferenceClientRect()
                : {
                    width: 0,
                    height: 0,
                    left: 0,
                    top: 0,
                    right: 0,
                    bottom: 0,
                  }
              popup?.[0].setProps({
                getReferenceClientRect,
              })
            },

            onKeyDown(props: SuggestionKeyDownProps) {
              if (props.event.key === 'Escape') {
                popup?.[0].hide()

                return true
              }

              if (!popup?.[0].state.isShown) {
                popup?.[0].show()
              }

              return component?.ref?.onKeyDown(props)
            },

            onExit(props) {
              popup?.[0].hide()
              if (scrollHandler) {
                const { view } = props.editor
                view.dom.parentElement?.removeEventListener('scroll', scrollHandler)
              }
              component?.destroy()
            },
          }
        },
      }),
    ]
  },

  addStorage() {
    return {
      rect: {
        width: 0,
        height: 0,
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
      },
    }
  },
})

export default SlashCommand
